import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  Section,
  SectionInner,
  SectionHead,
  SectionHeading,
} from "../../globalStyles";

const Group = styled.div`
  & + & {
    margin-top: var(--space-12);
  }
`;

const GroupTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: var(--font-weight-extrabold);
  letter-spacing: -0.02em;
  color: var(--text-primary);
  margin-bottom: var(--space-5);
`;

/* 상장·선정서를 스티커처럼 붙여 놓는다 */
const Stickers = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4) var(--space-3);
`;

const Sticker = styled.li`
  display: inline-flex;
  align-items: baseline;
  gap: 0.6rem;
  padding: 0.7rem 1rem 0.7rem 0.9rem;
  border: 2px solid var(--text-primary);
  border-radius: var(--radius-md);
  background: #ffffff;
  box-shadow: 4px 4px 0 var(--text-primary);
  word-break: keep-all;
`;

const Year = styled.span`
  font-size: 0.8125rem;
  font-weight: var(--font-weight-bold);
  color: var(--text-muted);
  white-space: nowrap;
`;

const Text = styled.span`
  font-size: 0.9375rem;
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  line-height: 1.5;
`;

const AchievementsSection = () => {
  const [language, setLanguage] = useState("ko");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/en") setLanguage("en");
    else if (location.pathname === "/ko") setLanguage("ko");
    else setLanguage(localStorage.getItem("language") || "ko");

    const handleLanguageChange = (e) => setLanguage(e.detail);
    window.addEventListener("languageChange", handleLanguageChange);
    return () =>
      window.removeEventListener("languageChange", handleLanguageChange);
  }, [location.pathname]);

  const content = {
    ko: {
      heading: "검증된 발자취",
      awardsTitle: "수상 기록",
      businessTitle: "사업 수행",
      awards: [
        { year: "2024", text: "머니투데이 대한민국 기업대상 우수스타트업대상" },
        { year: "2024", text: "코맥스 X 한양대학교 벤처창업경진대회 최우수상" },
        { year: "2024", text: "한양대학교 창업동아리 경진대회 대상" },
        { year: "2023", text: "한양 스타트업 아카데미 데모데이 우수상" },
        { year: "2023", text: "한국방송미디어공학회 대학생 논문 우수상" },
        { year: "2023", text: "한양대학교 X 코맥스 벤처창업경진대회 우수상" },
      ],
      business: [
        { year: "2026", text: "한국콘텐츠진흥원 글로벌 웹툰 게임스 선정" },
        { year: "2026", text: "중소벤처기업부 창업중심대학 지원기업 선정" },
        { year: "2026", text: "한양대학교 캠퍼스타운 입주기업 선정" },
        { year: "2025", text: "구글플레이 창구프로그램 7기 선정" },
        { year: "2025", text: "한국콘텐츠진흥원 게임더하기 지원사업 선정" },
        { year: "2025", text: "클라우드 서비스 보급·확산 사업 선정" },
        { year: "2024", text: "중소벤처기업부 창업중심대학 초기창업기업 지원 선정" },
        { year: "2024", text: "서울경제진흥원 게임 콘텐츠 맞춤형 지원 (글로벌)" },
        { year: "2024", text: "서울경제진흥원 게임 콘텐츠 맞춤형 지원 2차 1위 선정" },
      ],
    },
    en: {
      heading: "A proven track record",
      awardsTitle: "Awards",
      businessTitle: "Selected Programs",
      awards: [
        { year: "2024", text: "Best Startup, MoneyToday Korea Enterprise Awards" },
        { year: "2024", text: "Grand Prize, COMMAX x Hanyang Univ. Venture Startup Contest" },
        { year: "2024", text: "Grand Prize, Hanyang Univ. Startup Club Contest" },
        { year: "2023", text: "Excellence Award, Hanyang Startup Academy Demo Day" },
        { year: "2023", text: "Excellence Award, KIBME Student Paper" },
        { year: "2023", text: "Excellence Award, Hanyang Univ. x COMMAX Venture Contest" },
      ],
      business: [
        { year: "2026", text: "KOCCA Global Webtoon Games" },
        { year: "2026", text: "MSS Startup-Centered University program" },
        { year: "2026", text: "Hanyang Univ. Campustown resident company" },
        { year: "2025", text: "Google Play Changgu Program (7th)" },
        { year: "2025", text: "KOCCA Game Plus support program" },
        { year: "2025", text: "Cloud Service Adoption & Diffusion program" },
        { year: "2024", text: "MSS Startup-Centered University early-startup support" },
        { year: "2024", text: "SBA game content tailored support (Global)" },
        { year: "2024", text: "SBA game content tailored support, ranked 1st in round 2" },
      ],
    },
  };

  const c = content[language];

  return (
    <Section id="achievements" $rule>
      <SectionInner>
        <SectionHead>
          <SectionHeading>{c.heading}</SectionHeading>
        </SectionHead>
        <Group>
          <GroupTitle>{c.awardsTitle}</GroupTitle>
          <Stickers>
            {c.awards.map((a, i) => (
              <Sticker key={i}>
                <Year>{a.year}</Year>
                <Text>{a.text}</Text>
              </Sticker>
            ))}
          </Stickers>
        </Group>
        <Group>
          <GroupTitle>{c.businessTitle}</GroupTitle>
          <Stickers>
            {c.business.map((b, i) => (
              <Sticker key={i}>
                <Year>{b.year}</Year>
                <Text>{b.text}</Text>
              </Sticker>
            ))}
          </Stickers>
        </Group>
      </SectionInner>
    </Section>
  );
};

export default AchievementsSection;
